import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import Routes, { DELIVERY_ID_PARAM } from 'routes';
import usePrintAllTrayImages from 'api/hooks/usePrintAllTrayImages';
import theme from 'App.theme';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import { useLanguage } from 'context/AppContext';
import PrintTrayLabelButton from 'components/PrintTrayLabelButton';

type Props = {
  deliveryId: string;
  orderRecordId?: string;
  onClose?: VoidFunction;
};

export const DeliveryCheckedInSetsDialog = ({ deliveryId, orderRecordId, onClose }: Props) => {
  const lang = useLanguage()[0].DeliveryCheckedInSetsDialog;

  const navigate = useNavigate();

  const onViewDeliveryInfoPage = useCallback(() => {
    navigate(Routes.CheckOut.path.replace(DELIVERY_ID_PARAM, deliveryId));
  }, [deliveryId, navigate]);

  const onViewSurgeryInfoPage = useCallback(() => {
    navigate(Routes.Surgery.makePath(orderRecordId));
  }, [navigate, orderRecordId]);

  const onGoBackToDeliveryList = useCallback(() => {
    navigate(Routes.Home.path);
  }, [navigate]);

  const { openTrayImageInNewTab, loading: trayImageLoading } = usePrintAllTrayImages(deliveryId, {
    kitInstanceId: undefined,
    trayInstanceId: undefined,
  });

  const onPrintAllDeliveryInformation = useCallback(async () => {
    await openTrayImageInNewTab();
  }, [openTrayImageInNewTab]);

  return (
    <Dialog open title={lang.title} heading={lang.heading} onClose={onClose}>
      <div
        style={{
          marginTop: theme.layout.spacing.medium,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.layout.spacing.medium,
        }}
      >
        <CheckCircleOutlineIcon
          sx={{
            width: '4em',
            height: '4em',
            color: theme => theme.colors.success,
            alignSelf: 'center',
            marginBottom: theme => theme.layout.spacing.tiny,
          }}
        />
        <Button colors="white on active" onClick={onViewDeliveryInfoPage}>
          {lang.viewDeliveryInfoPage}
        </Button>

        {!!orderRecordId && (
          <Button colors="white on active" onClick={onViewSurgeryInfoPage}>
            {lang.viewSurgeryInfoPage}
          </Button>
        )}

        <Button colors="white on active" onClick={onGoBackToDeliveryList}>
          {lang.goBackToDeliveryList}
        </Button>

        <Button
          label={lang.printAllDeliveryInformation}
          onClick={onPrintAllDeliveryInformation}
          colors="white on active"
          disabled={trayImageLoading}
          loading={trayImageLoading}
        />

        <PrintTrayLabelButton deliveryId={deliveryId} width="100%" />
      </div>
    </Dialog>
  );
};
